import barba from "@barba/core";
import { initAos } from "./aos";
import { initCounter } from "./counter";
import { initLightbox } from "./lightbox";
import { initLazyload } from "./lazyload";
import { initVideo } from "./video";
import { initVersionChecker } from "./typo3-version-check";
import { closeHeaderMenu, initHeader, initHeaderSticky, getPageHeader } from "./header";
import { initAccessibility } from "./accessibility";
import { initPageModules } from "./page-init";
import { initFriendlyCaptcha } from "./friendly-captcha";
import { isFlickityDragActive } from "./flickity-drag-state";

const SITE_HOST_PATTERN = /^(www\.)?nitsantech\.de$|\.ddev\.site$/i;

const isModifiedEvent = (event) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const isKnownSiteHost = (hostname) => SITE_HOST_PATTERN.test(hostname);

const toCurrentOriginHref = (href) => {
  if (!href || href.startsWith("#") || /^(mailto:|tel:|javascript:)/i.test(href)) {
    return null;
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.origin === window.location.origin) {
      return null;
    }
    if (!isKnownSiteHost(url.hostname)) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const normalizeLinkHrefs = () => {
  document.querySelectorAll('a[href^="http"]').forEach((link) => {
    const relative = toCurrentOriginHref(link.getAttribute("href"));
    if (relative) {
      link.setAttribute("href", relative);
    }
  });
};

const isSamePageLink = (href) => {
  if (!href || href.startsWith("#")) {
    return false;
  }

  try {
    const url = new URL(href, window.location.href);
    const current = new URL(window.location.href);
    return (
      url.origin === current.origin &&
      url.pathname === current.pathname &&
      url.search === current.search
    );
  } catch {
    return false;
  }
};

const shouldPreventBarba = ({ el, event, href }) => {
  if (!el) {
    return true;
  }

  if (isFlickityDragActive()) {
    return true;
  }

  if (event && (event.button !== 0 || isModifiedEvent(event))) {
    return true;
  }

  if (
    el.closest("[data-barba-prevent]") ||
    el.classList.contains("lang-menu-item") ||
    el.classList.contains("filterBlogs") ||
    el.classList.contains("blogpagination__link")
  ) {
    return true;
  }

  const linkHref = href || el.getAttribute("href") || el.href || "";

  if (!linkHref || linkHref === "#") {
    return true;
  }

  const target = el.getAttribute("target");
  const download = el.hasAttribute("download");
  const rel = (el.getAttribute("rel") || "").toLowerCase();

  if (target && target !== "_self") return true;
  if (download) return true;
  if (rel.includes("external")) return true;

  if (el.hasAttribute("data-fancybox") || el.hasAttribute("data-bs-toggle")) {
    return true;
  }

  let url;
  try {
    url = new URL(linkHref, window.location.href);
  } catch {
    return true;
  }

  const isHashOnly = linkHref.startsWith("#");
  const isProtocolLink = /^(mailto:|tel:|javascript:)/i.test(linkHref);
  const isExternal =
    url.origin !== window.location.origin && !isKnownSiteHost(url.hostname);

  if (isExternal || isHashOnly || isSamePageLink(linkHref) || isProtocolLink) {
    return true;
  }

  return false;
};

const copyElementAttributes = (from, to) => {
  [...from.attributes].forEach((attr) => {
    to.setAttribute(attr.name, attr.value);
  });
};

const replaceHeadNodes = (selector, nextDoc) => {
  const nextNodes = [...nextDoc.head.querySelectorAll(selector)];
  const currentNodes = [...document.head.querySelectorAll(selector)];

  currentNodes.forEach((node) => node.remove());
  nextNodes.forEach((node) => {
    document.head.appendChild(document.importNode(node, true));
  });
};

const syncHeadLink = (rel, nextDoc) => {
  const nextLink = nextDoc.head.querySelector(`link[rel="${rel}"]`);
  const currentLink = document.head.querySelector(`link[rel="${rel}"]`);

  if (nextLink && currentLink) {
    copyElementAttributes(nextLink, currentLink);
    return;
  }

  if (nextLink && !currentLink) {
    document.head.appendChild(document.importNode(nextLink, true));
    return;
  }

  if (!nextLink && currentLink) {
    currentLink.remove();
  }
};

const parseNextDocument = (data) => {
  const html = data?.next?.html;
  if (!html) {
    return null;
  }

  return new DOMParser().parseFromString(html, "text/html");
};

const syncPageMetadata = (data) => {
  const doc = parseNextDocument(data);
  if (!doc) {
    return;
  }

  const nextTitle = doc.querySelector("title")?.textContent?.trim();
  if (nextTitle) {
    document.title = nextTitle;
  }

  const nextLang = doc.documentElement.getAttribute("lang");
  if (nextLang) {
    document.documentElement.setAttribute("lang", nextLang);
  }

  replaceHeadNodes('meta[name="description"]', doc);
  replaceHeadNodes('meta[name="robots"]', doc);
  replaceHeadNodes('meta[property^="og:"]', doc);
  replaceHeadNodes('meta[name^="twitter:"]', doc);

  syncHeadLink("canonical", doc);
  replaceHeadNodes('link[rel="alternate"]', doc);

  const nextBodyClass = doc.body?.className?.trim();
  if (nextBodyClass) {
    document.body.className = nextBodyClass;
  }
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

let progressBar = null;
let progressTimer = null;
let progressStartTime = 0;
let progressCompleteTimer = null;

const MIN_PROGRESS_VISIBLE_MS = 400;

const createLoadingProgressBar = () => {
  if (progressBar) {
    return progressBar;
  }

  progressBar = document.createElement("div");
  progressBar.className = "barba-progress-bar";
  progressBar.setAttribute("role", "progressbar");
  progressBar.setAttribute("aria-hidden", "true");
  document.body.appendChild(progressBar);

  return progressBar;
};

const setLoadingProgress = (value) => {
  const bar = createLoadingProgressBar();
  const percent = Math.min(100, Math.max(0, value));
  bar.style.setProperty("--barba-progress", String(percent / 100));
  bar.setAttribute("aria-valuenow", String(Math.round(percent)));
};

const stopLoadingProgressTimer = () => {
  if (progressTimer) {
    window.clearInterval(progressTimer);
    progressTimer = null;
  }
};

const clearProgressCompleteTimer = () => {
  if (progressCompleteTimer) {
    window.clearTimeout(progressCompleteTimer);
    progressCompleteTimer = null;
  }
};

const startLoadingProgress = () => {
  const bar = createLoadingProgressBar();
  clearProgressCompleteTimer();
  stopLoadingProgressTimer();
  progressStartTime = Date.now();

  bar.classList.add("barba-progress-bar--active");
  setLoadingProgress(0);
  // Force layout so the first width increase animates visibly.
  bar.offsetWidth;
  requestAnimationFrame(() => setLoadingProgress(12));

  let current = 12;
  progressTimer = window.setInterval(() => {
    if (current < 90) {
      if (current < 25) {
        current += 8;
      } else if (current < 60) {
        current += 3;
      } else {
        current += 1;
      }
      setLoadingProgress(current);
    }
  }, 140);
};

const completeLoadingProgress = () => {
  const elapsed = Date.now() - progressStartTime;
  const delay = Math.max(0, MIN_PROGRESS_VISIBLE_MS - elapsed);

  clearProgressCompleteTimer();
  progressCompleteTimer = window.setTimeout(() => {
    progressCompleteTimer = null;
    stopLoadingProgressTimer();
    setLoadingProgress(100);

    const bar = createLoadingProgressBar();
    window.setTimeout(() => {
      bar.classList.remove("barba-progress-bar--active");
      setLoadingProgress(0);
    }, 350);
  }, delay);
};

const resetLoadingProgress = () => {
  clearProgressCompleteTimer();
  stopLoadingProgressTimer();
  if (!progressBar) {
    return;
  }

  progressBar.classList.remove("barba-progress-bar--active");
  setLoadingProgress(0);
};

const handleInternalNavClick = (event) => {
  const link = event.target.closest?.("a[href]");
  if (!link) {
    return;
  }

  if (isFlickityDragActive()) {
    return;
  }

  const href = link.getAttribute("href");

  if (isSamePageLink(href)) {
    event.preventDefault();
    if (href?.includes("#")) {
      const url = new URL(href, window.location.href);
      if (url.hash) {
        document.querySelector(url.hash)?.scrollIntoView({ behavior: "smooth" });
      }
    }
    return;
  }

  if (shouldPreventBarba({ el: link, event, href })) {
    return;
  }

  // Progress is started in barba.hooks.before when a transition actually runs.
};

const reInitUi = () => {
  initAos();
  initCounter();
  initLightbox();
  initLazyload();
  initVideo();
  initVersionChecker();
  initHeader();
  initPageModules();
  initAccessibility();
  document.dispatchEvent(new CustomEvent("barba:after-enter"));
};

const initBarba = () => {
  if (window.__barbaInitialized || !document.querySelector('[data-barba="wrapper"]')) {
    return;
  }

  window.__barbaInitialized = true;

  normalizeLinkHrefs();

  document.addEventListener(
    "click",
    (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link) {
        return;
      }
      const relative = toCurrentOriginHref(link.getAttribute("href"));
      if (relative) {
        link.setAttribute("href", relative);
      }
    },
    true
  );

  document.addEventListener("click", handleInternalNavClick, true);

  document.addEventListener("form:submit-start", () => {
    startLoadingProgress();
  });

  document.addEventListener("form:submit-end", () => {
    resetLoadingProgress();
  });

  document.addEventListener("form:navigate", (event) => {
    const url = event.detail?.url;
    if (!url) {
      return;
    }

    closeHeaderMenu();
    startLoadingProgress();
    window.__formNavigationActive = true;
    window.__pendingFormNavigateUrl = url;

    const clearFormNavigationState = () => {
      window.__formNavigationActive = false;
      window.__pendingFormNavigateUrl = null;
    };

    const fallbackTimer = setTimeout(() => {
      if (window.__formNavigationActive && window.__pendingFormNavigateUrl) {
        const fallbackUrl = window.__pendingFormNavigateUrl;
        clearFormNavigationState();
        window.location.assign(fallbackUrl);
      }
    }, 3000);

    try {
      const navigation = barba.go(url);

      if (navigation && typeof navigation.then === "function") {
        navigation
          .then(() => {
            clearTimeout(fallbackTimer);
            clearFormNavigationState();
          })
          .catch(() => {
            clearTimeout(fallbackTimer);
            const fallbackUrl = window.__pendingFormNavigateUrl || url;
            clearFormNavigationState();
            window.location.assign(fallbackUrl);
          });
        return;
      }

      clearTimeout(fallbackTimer);
      clearFormNavigationState();
    } catch {
      clearTimeout(fallbackTimer);
      clearFormNavigationState();
      window.location.assign(url);
    }
  });

  barba.hooks.before(() => {
    closeHeaderMenu();
    startLoadingProgress();
    scrollToTop();

    const header = getPageHeader();
    if (header) {
      header.classList.remove('header--unpinned', 'header-sticky');
      header.classList.add('header--pinned');
    }
  });

  barba.hooks.afterEnter((data) => {
    completeLoadingProgress();
    scrollToTop();
    syncPageMetadata(data);
    normalizeLinkHrefs();

    requestAnimationFrame(() => {
      initFriendlyCaptcha();
      reInitUi();
      initHeaderSticky();
    });
  });

  barba.init({
    prevent: shouldPreventBarba,
    preventRunning: false,
    timeout: 10000,
    requestError: (error, action, url) => {
      console.warn("[barba] request failed", { action, url, error });
      resetLoadingProgress();

      if (window.__formNavigationActive && window.__pendingFormNavigateUrl) {
        const fallbackUrl = window.__pendingFormNavigateUrl;
        window.__formNavigationActive = false;
        window.__pendingFormNavigateUrl = null;
        window.location.assign(fallbackUrl);
        return false;
      }

      return false;
    },
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBarba, { once: true });
} else {
  initBarba();
}
